import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed, get } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { PrismDataBar } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type {
  BarData,
  SymmetryData,
} from '@mylighthouse/prism-ember/types/components/prism/data-bar';
import { and, or } from 'ember-truth-helpers';
import { IncrementFor } from 'frontend-utils';
import { camelize } from 'frontend-utils/utils/convert-case';
import {
  COLOR_BOOKING_LY,
  COLOR_TEXT_DEFAULT,
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';
import HotelAndMarketRevenueInsightBaseSegmentationBlockItemBase, {
  type HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature,
  type SegmentationItem,
} from 'lumen/components/hotel-and-market/revenue-insight/base/segmentation-block-item-base';
import HotelAndMarketRevenueInsightSegmentationSegmentationMetricBlockBarLoading from 'lumen/components/hotel-and-market/revenue-insight/segmentation/segmentation-metric-block-bar-loading';
import HotelAndMarketRevenueInsightTooltipsTooltipRevenueInsight from 'lumen/components/hotel-and-market/revenue-insight/tooltips/tooltip-revenue-insight';
import type { RevenueInsightSegmentationValue } from 'lumen/models';
import {
  type Kpi,
  KPI_CATEGORIES,
  type KpiCategory,
  KPIS,
} from 'lumen/utils/constants/ri-constant-values';
import kpiCategoryDisplayNameFormatter from 'lumen/utils/revenue-insight/kpi-category-display-name-formatter';

interface ExtendedSegmentationItem extends SegmentationItem {
  isTransientAndGroupFilterDisabled: boolean;
  kpi: Kpi;
  kpiCategory: KpiCategory;
  segmentation: RevenueInsightSegmentationValue;
}

interface HotelAndMarketRevenueInsightKpiKpiListItemSignature extends HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature {
  Args: HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature['Args'] & {
    item: ExtendedSegmentationItem;
    params: HotelAndMarketRevenueInsightBaseSegmentationBlockItemBaseSignature['Args']['params'] & {
      changeSelectedKpi: (kpi: Kpi) => void;
      changeSelectedKpiCategory: (kpiCategory: KpiCategory) => void;
      isLoading: boolean;
      kpiCategory: KpiCategory;
      visibleKpis: Kpi[];
    };
  };
}

export default class HotelAndMarketRevenueInsightKpiKpiListItem extends HotelAndMarketRevenueInsightBaseSegmentationBlockItemBase<HotelAndMarketRevenueInsightKpiKpiListItemSignature> {
  @service declare usageAnalytics: Services['usage-analytics'];

  titleColor = getCssVariableValue(COLOR_TEXT_DEFAULT);

  @computed('args.item.calculatedValues.change', 'metricKey')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChangeValue() {
    return (
      get(this, `args.item.calculatedValues.change.${this.metricKey}`) || 0
    );
  }

  @computed('args.{maxValues,item.kpi}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMaxChange() {
    return (
      (get(this.args, `maxValues.${this.args.item?.kpi}.change`) as number) || 1
    );
  }

  @computed('args.{maxValues,item.kpi}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMaxValue() {
    return (
      (get(this.args, `maxValues.${this.args.item?.kpi}.BOB`) as number) || 1
    );
  }

  @computed('args.item.calculatedValues.reference', 'metricKey')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedReferenceValue() {
    return (
      get(this, `args.item.calculatedValues.reference.${this.metricKey}`) || 0
    );
  }

  @computed('args.item.{isTransientAndGroupFilterDisabled,kpiCategory}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    const isUnmapped = this.args.item?.kpiCategory === KPI_CATEGORIES.UNMAPPED;
    return isUnmapped || this.args.item?.isTransientAndGroupFilterDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isItemPresent() {
    return Object.keys(this.args.item).length !== 0;
  }

  @computed('args.item.{kpi,kpiCategory}', 'args.params.{kpi,kpiCategory}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSelected() {
    return (
      this.args.item?.kpi === this.args.params?.kpi &&
      this.args.item?.kpiCategory === this.args.params?.kpiCategory
    );
  }

  @computed(
    'args.item.kpiCategory',
    'intl',
    'riSelections.isCancellationsOnlyView',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiCategoryDisplayName() {
    return kpiCategoryDisplayNameFormatter(
      this.args.item?.kpiCategory,
      this.riSelections,
      this.intl,
      true,
    );
  }

  @computed('args.{params.visibleKpis,item.kpi}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpis() {
    return this.args.params.visibleKpis ?? [this.args.item?.kpi];
  }

  // Shared with segmentation-block-item-base.js.
  // For KpiListItems, we use the long keys e.g. nrOfRoomsTotal, revenueTransient.
  @computed('args.item.{kpi,kpiCategory}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get metricKey() {
    return camelize(`${this.args.item?.kpi}_${this.args.item?.kpiCategory}`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceTitle() {
    if (this.riSelections?.isBudgetActive) {
      return this.riSelections?.vsBudgetText;
    }

    if (this.riSelections?.isForecastActive) {
      return this.riSelections?.vsForecastText;
    }

    return this.riSelections?.vsReferencePeriodText;
  }

  @computed('args.item.kpi', 'riSelections.isCancellationsPercentageView')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPercentages() {
    return (
      this.args.item?.kpi === KPIS.OCCUPANCY ||
      this.riSelections.isCancellationsPercentageView
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTarget() {
    return !this.isDisabled && Number.isFinite(this.targetValue);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get symmetryData() {
    return {
      labelColor: this.titleColor,
      value: this.changeValue,
      formattedValue: this.formattedChangeValueWithPercentage,
      positiveWidthPercentage: this.positiveReferenceValuePercentage,
      negativeWidthPercentage: this.negativeReferenceValuePercentage,
    } as SymmetryData;
  }

  @computed(
    'args.item.kpi',
    'pickupLastYearPercentage',
    'riSelections.{isBudgetActive,isForecastActive,isTargetActive}',
    'valuePercentage',
  )
  get valueProgressBarData(): BarData[] {
    const valueProgressBarData: BarData[] = [
      {
        widthPercentage: this.valuePercentage,
        backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
      },
    ];

    // Don't show Pickup Last Year for ADR
    if (this.riSelections.isTargetActive && this.args.item?.kpi !== KPIS.ADR) {
      valueProgressBarData.push({
        widthPercentage: this.pickupLastYearPercentage,
        backgroundColor: getCssVariableValue(COLOR_BOOKING_LY),
      });
    }

    return valueProgressBarData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get vsReferencePeriodText() {
    return this.riSelections?.vsReferencePeriodText;
  }

  @action changeSelectedKpiAndKpiCategory(): void {
    if (!this.isDisabled) {
      this.args.params?.changeSelectedKpi(this.args.item?.kpi);
      this.args.params?.changeSelectedKpiCategory(this.args.item?.kpiCategory);

      this.usageAnalytics.track('KPI Displayed Changed', {
        eventTrigger: 'kpi-table-click',
        kpiName: this.args.item.kpi,
        kpiCategory: this.args.item.kpiCategory,
      });
    }
  }

  <template>
    {{#if @params.isLoading}}
      <div class="block-list-item-container">
        <HotelAndMarketRevenueInsightSegmentationSegmentationMetricBlockBarLoading
        />
      </div>
    {{else}}
      <button
        class="button--unstyled block-list-item-container
          {{if
            this.isDisabled
            'segmentation-item--disabled'
            'segmentation-item--selectable'
          }}
          {{if this.isSelected 'segmentation-item--selected'}}"
        data-event-id="hotel-revenue-insight-kpi-select"
        data-event-value={{this.metricKey}}
        disabled={{this.isDisabled}}
        type="button"
        {{on "click" this.changeSelectedKpiAndKpiCategory}}
        {{on "mouseenter" this.onMouseEnterTask.perform}}
        {{on "mouseleave" this.onMouseLeaveTask.perform}}
      >
        <IncrementFor @times={{this.indentationAmount}}>
          <div class="indent-container"></div>
        </IncrementFor>
        {{#if
          (or
            this.riSelections.isTargetActive
            this.riSelections.isCancellationsOnlyView
          )
        }}
          <div class="current-value current-value--target">
            <PrismDataBar
              @data={{this.valueProgressBarData}}
              @isStriped={{this.riSelections.isCancellationsOnlyView}}
              @targetValue={{if this.showTarget this.targetValuePercentage}}
              as |DataBar|
            >
              <DataBar.label>
                <span class="text-200--medium">
                  {{this.kpiCategoryDisplayName}}
                </span>
              </DataBar.label>
              <DataBar.value @text={{this.formattedValueWithPercentage}} />
            </PrismDataBar>
          </div>
          {{! Note: reference-value class is used as selector in IC RI single cancellations product tour }}
          <div class="reference-value reference-value--target">
            <div class="reference-value__title">{{this.referenceTitle}}</div>
            <div
              style={{this.valueColor}}
            >{{this.formattedChangeValueWithPercentage}}</div>
          </div>
        {{else}}
          <div class="current-value">
            <PrismDataBar @data={{this.valueProgressBarData}} as |DataBar|>
              <DataBar.label>
                <span class="text-200--medium">
                  {{this.kpiCategoryDisplayName}}
                </span>
              </DataBar.label>
              <DataBar.value @text={{this.formattedValueWithPercentage}} />
            </PrismDataBar>
          </div>
          <div class="reference-value">
            <PrismDataBar @data={{this.symmetryData}} @type="symmetric" />
          </div>
        {{/if}}
      </button>
    {{/if}}
    {{#if (and this.isHovered this.isItemPresent)}}
      <EmberTooltip
        @isShown={{this.isHovered}}
        @side="right"
        @tooltipClass="base-tooltip revenue-insight-segmentation-value-tooltip"
      >
        <HotelAndMarketRevenueInsightTooltipsTooltipRevenueInsight
          @data={{@item.segmentation}}
          @hideTitle={{true}}
          @kpiCategory={{@item.kpiCategory}}
          @kpis={{this.kpis}}
          @metricKey={{this.metricKey}}
        />
      </EmberTooltip>
    {{/if}}
  </template>
}
